import Cookies from 'js-cookie'
import { removeVliveId } from '@/utils/vlive_id'

const tokenKey = 'VLIVE_SESSION_TOKEN'
const tokenKeyForWeb = 'VLIVE_WEB_SESSION_TOKEN'
const revokedTokensKey = 'VLIVE_REVOKED_SESSION_TOKENS'

// セッション無効を表すサーバのエラーコード (期限切れ・不明トークン共通)
export const invalidSessionErrorCode = 1359020083

// セッション無効 (invalidSessionErrorCode) の 401 のときだけトークンを無効化する
export function shouldRevokeToken(errorCode?: number) {
  return errorCode === invalidSessionErrorCode
}

// 無効化したトークンの保持数の上限
// アプリ注入 / Web / クエリ (?st=) のトークンが順に 401 になっても取りこぼさない程度
const revokedTokensLimit = 5

// localStorage が使えない環境向けに、同じページ内ではメモリ上にも記録しておく
// (ログイン画面への遷移は router.push() でページを読み直さないので、これだけでループは止まる)
let revokedTokensInMemory: string[] = []

// WebView やプライベートブラウズでは localStorage へのアクセスが例外になることがある
// isRevokedToken() は getToken() から毎回呼ばれるため、ここで落とすと全ページが壊れる
// 参照できない場合は、記録なし (従来どおりの挙動) にする
function readRevokedTokens(): string[] {
  try {
    const raw = localStorage.getItem(revokedTokensKey)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

// 401 経由でログアウトしたトークンを記録する
// アプリ注入トークンは Web から削除できないため、後から別のトークンを無効化しても
// 先に無効化したトークンが復活しないように、上書きせず直近の数件を保持する
export function revokeToken(token: string) {
  const tokens = readRevokedTokens()
    .concat(revokedTokensInMemory)
    .filter((t, i, all) => t !== token && all.indexOf(t) === i)
  tokens.push(token)
  revokedTokensInMemory = tokens.slice(-revokedTokensLimit)

  try {
    localStorage.setItem(
      revokedTokensKey,
      JSON.stringify(revokedTokensInMemory)
    )
  } catch (e) {
    // 記録できなくてもログアウト処理自体は続行する
  }
}

export function isRevokedToken(token: string | undefined) {
  if (token === undefined) {
    return false
  }
  return (
    revokedTokensInMemory.includes(token) || readRevokedTokens().includes(token)
  )
}

// 無効化済みのトークンを認証用途から取り除く
function excludeRevoked(token: string | undefined) {
  return isRevokedToken(token) ? undefined : token
}

export function getToken() {
  return (
    excludeRevoked(getTokenFromQuery()) ||
    excludeRevoked(getTokenFromCookie()) ||
    excludeRevoked(getTokenForWebapp())
  )
}

export function getTokenFromQuery() {
  return new URLSearchParams(location.search).get('st') || undefined
}

// アプリから渡されるトークン
// WebView 判定 (Cookie の有無) にも使われるため、無効化済みでもそのまま返す
export function getTokenFromCookie() {
  return Cookies.get(tokenKey) || undefined
}

// For Webで使用するトークン
export function getTokenForWebapp() {
  // セッションが保持されていれば優先的に使用
  let token = sessionStorage.getItem(tokenKey) || undefined
  if (token !== undefined) {
    return token
  }

  token = Cookies.get(tokenKeyForWeb) || undefined
  if (token !== undefined) {
    sessionStorage.setItem(tokenKey, token)
  }

  return token
}

export function saveToken(token: string) {
  // タブ内でのセッションを保持
  sessionStorage.setItem(tokenKey, token)

  // セッションの有効期限に合わせて1ヶ月にする
  Cookies.set(tokenKeyForWeb, token, { path: '/', expires: 30, secure: true })

  // セッションに紐づいているvlive_idを削除
  removeVliveId()
}

export function removeToken() {
  sessionStorage.removeItem(tokenKey)
  Cookies.remove(tokenKeyForWeb, { path: '/' })
  // おまじない(どこかのページで開発時に手動でコンソールからcookieをセットすると
  // 何故かこのpathにcookieが残るので削除しておく)
  Cookies.remove(tokenKeyForWeb, { path: '/login' })

  // セッションに紐づいているvlive_idを削除
  removeVliveId()
}

// Viewerの場合はアプリ側からCookie経由でトークンが渡される
export function getViewerToken() {
  return excludeRevoked(getTokenFromCookie())
}

// Webappの場合はブラウザのCookie経由でトークンが渡される
export function getWebappToken() {
  return excludeRevoked(getTokenForWebapp())
}

// api()がAuthorizationヘッダーに使用するトークンを取得する(Viewer優先)
export function getApiAuthToken() {
  return getViewerToken() || getWebappToken()
}

// api()がAuthorizationヘッダーを付与できるトークンを保持しているか
export function hasApiAuthToken() {
  return getApiAuthToken() !== undefined
}
